import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Briefcase } from 'lucide-react';
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, isSameDay, isSameMonth,
} from 'date-fns';
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from './ui/resizable';

// Format "HH:mm" → "h:mm AM/PM"
const formatTime12h = (t) => {
  if (!t || typeof t !== 'string') return t || '';
  const m = t.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return t;
  let h = parseInt(m[1], 10);
  const min = m[2];
  const period = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${min} ${period}`;
};

const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isDesktop;
};

/**
 * V2 Calendar — clean mini-month + day-list layout.
 *
 * Mobile (<768px): single column, mini-month on top, day content below.
 * Desktop (≥768px): resizable 2-column layout (mini-month | day content).
 *   The split is persisted to localStorage so each user gets their preferred
 *   ratio next time they open the calendar.
 *
 * Tapping the jobs roll-up opens the existing V1 day-sheet dialog so the
 * job list and per-job action dialog stay identical to V1.
 */
export default function V2MobileCalendar({
  cursorMonth,
  onPrevMonth,
  onNextMonth,
  onJumpToToday,
  selectedDay,
  onSelectDay,
  getJobsForDate,
  getEventsForDate,
  getEventColor,
  getEventTextColor,
  getContrastTextColor,
  onOpenEvent,
  onOpenJobsList,
}) {
  const monthStart = startOfMonth(cursorMonth);
  const monthEnd = endOfMonth(cursorMonth);
  const gridStart = startOfWeek(monthStart);
  const gridEnd = endOfWeek(monthEnd);
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });
  const today = new Date();
  const isDesktop = useIsDesktop();

  const dayJobs = getJobsForDate ? getJobsForDate(selectedDay) || [] : [];
  const dayEvents = getEventsForDate ? getEventsForDate(selectedDay) || [] : [];

  // Persisted left-panel size for the desktop resizable split.
  // Default 42% — gives the mini-month enough room without crowding the day list.
  const savedSize = (() => {
    try {
      const v = parseFloat(localStorage.getItem('v2cal.leftPanelSize'));
      return Number.isFinite(v) && v >= 20 && v <= 70 ? v : 42;
    } catch { return 42; }
  })();

  const leftPanel = (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-2.5 md:p-4 h-full">
      <div className="flex items-center justify-between mb-2 md:mb-3">
        <button
          type="button"
          onClick={onPrevMonth}
          className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
          aria-label="Previous month"
          data-testid="v2-mini-prev"
        >
          <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
        </button>
        <p className="text-sm md:text-base font-bold tracking-wide uppercase font-['Barlow_Condensed'] text-slate-900">
          {format(cursorMonth, 'MMMM yyyy')}
        </p>
        <button
          type="button"
          onClick={onNextMonth}
          className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
          aria-label="Next month"
          data-testid="v2-mini-next"
        >
          <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-0.5 md:gap-1 text-[9px] md:text-[11px] uppercase tracking-wide text-slate-400 mb-0.5 md:mb-1">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <div key={i} className="text-center font-semibold">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5 md:gap-1">
        {days.map((d) => {
          const dateKey = format(d, 'yyyy-MM-dd');
          const inMonth = isSameMonth(d, cursorMonth);
          const isSelected = isSameDay(d, selectedDay);
          const isToday = isSameDay(d, today);
          const jobsCount = getJobsForDate ? getJobsForDate(d).length : 0;
          const eventsCount = getEventsForDate ? getEventsForDate(d).length : 0;
          const hasContent = (jobsCount + eventsCount) > 0;

          const cls = [
            'h-8 md:h-14 w-full rounded-md md:rounded-lg flex flex-col items-center justify-center text-xs md:text-sm transition-colors relative',
            inMonth ? 'text-slate-800' : 'text-slate-300',
            isSelected
              ? 'bg-amber-400 text-slate-900 font-bold shadow-sm'
              : isToday
                ? 'ring-1 ring-amber-300 hover:bg-slate-100'
                : 'hover:bg-slate-100',
          ].join(' ');

          return (
            <button
              key={dateKey}
              type="button"
              onClick={() => onSelectDay(d)}
              className={cls}
              data-testid={`v2-mini-day-${dateKey}`}
            >
              <span className="md:text-base md:font-semibold">{format(d, 'd')}</span>
              {hasContent && !isSelected && (
                <>
                  {/* Mobile: single amber dot */}
                  <span className="md:hidden absolute bottom-0.5 w-1 h-1 rounded-full bg-amber-500" />
                  {/* Desktop: count pill */}
                  <span className="hidden md:inline-block text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded mt-0.5 leading-none">
                    {jobsCount + eventsCount}
                  </span>
                </>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-end mt-1 md:mt-2">
        <button
          type="button"
          onClick={onJumpToToday}
          className="text-[11px] md:text-xs text-amber-600 hover:text-amber-700 font-semibold"
          data-testid="v2-mini-today"
        >
          Today
        </button>
      </div>
    </div>
  );

  const rightPanel = (
    <div className="space-y-3 md:space-y-3 h-full">
      {/* Selected day header */}
      <div className="px-1 md:px-0">
        <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
          {format(selectedDay, 'EEEE')}
        </p>
        <h2 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Barlow_Condensed'] leading-tight">
          {format(selectedDay, 'MMMM d, yyyy')}
        </h2>
      </div>

      {/* Day content: events first, then jobs roll-up */}
      <div className="space-y-2">
        {dayEvents.map((ev) => {
          const bg = ev.color || getEventColor(ev.type);
          const fg = ev.color
            ? getContrastTextColor(ev.color)
            : (ev.text_color || getEventTextColor(ev.type));
          const timeLabel = !ev.all_day && ev.start_time
            ? formatTime12h(ev.start_time)
            : (ev.all_day ? 'All day' : '');
          return (
            <button
              key={`v2-event-${ev.id}`}
              type="button"
              onClick={() => onOpenEvent(ev)}
              className="w-full text-left rounded-lg p-3 md:p-4 flex items-start gap-3 active:scale-[0.99] transition shadow-sm hover:shadow-md"
              style={{ backgroundColor: bg, color: fg }}
              data-testid={`v2-day-event-${ev.id}`}
            >
              <CalendarIcon className="w-5 h-5 flex-shrink-0 mt-0.5 opacity-80" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-bold uppercase tracking-wider opacity-80">
                    {ev.type || 'Event'}
                  </span>
                  {timeLabel && (
                    <span className="text-[11px] font-semibold opacity-90">{timeLabel}</span>
                  )}
                </div>
                <p className="font-semibold leading-snug mt-0.5">{ev.title}</p>
                {ev.notes && (
                  <p className="text-xs opacity-80 mt-1 line-clamp-2">{ev.notes}</p>
                )}
              </div>
            </button>
          );
        })}

        {dayJobs.length > 0 ? (
          <button
            type="button"
            onClick={onOpenJobsList}
            className="w-full text-left rounded-lg p-3 md:py-2.5 md:px-4 bg-amber-500 text-slate-900 shadow-sm flex items-center justify-between active:scale-[0.99] hover:shadow-md transition"
            data-testid="v2-day-jobs-rollup"
          >
            <div className="flex items-center gap-3 min-w-0">
              <Briefcase className="w-5 h-5 md:w-5 md:h-5 flex-shrink-0" />
              <div className="min-w-0 flex items-baseline gap-2 flex-wrap">
                <span className="text-base md:text-base font-bold leading-tight">
                  {dayJobs.length} {dayJobs.length === 1 ? 'Job' : 'Jobs'} Scheduled
                </span>
                <span className="text-[11px] opacity-75 hidden md:inline">Click to view details</span>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 flex-shrink-0" />
          </button>
        ) : dayEvents.length === 0 ? (
          <div
            className="text-center text-sm text-slate-400 py-6 md:py-10 md:bg-slate-50 md:rounded-lg md:border md:border-dashed md:border-slate-200"
            data-testid="v2-day-empty"
          >
            Nothing scheduled.
          </div>
        ) : null}
      </div>
    </div>
  );

  // Desktop: resizable 2-column layout. Mobile: stacked.
  if (isDesktop) {
    return (
      <div className="min-h-[520px]" data-testid="v2-mobile-calendar">
        <ResizablePanelGroup
          direction="horizontal"
          onLayout={(sizes) => {
            try {
              if (Array.isArray(sizes) && sizes[0]) {
                localStorage.setItem('v2cal.leftPanelSize', String(sizes[0]));
              }
            } catch { /* ignore */ }
          }}
          className="min-h-[520px]"
        >
          <ResizablePanel
            defaultSize={savedSize}
            minSize={28}
            maxSize={65}
            className="pr-3"
          >
            {leftPanel}
          </ResizablePanel>
          <ResizableHandle withHandle className="bg-slate-200" />
          <ResizablePanel defaultSize={100 - savedSize} minSize={35} className="pl-3">
            {rightPanel}
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    );
  }

  // Mobile fallback
  return (
    <div className="space-y-3" data-testid="v2-mobile-calendar">
      {leftPanel}
      {rightPanel}
    </div>
  );
}
